export type PromiseThrottleOptions = {
  requestsPerSecond: number
}

type QueuedTask<T> = {
  resolve: (value: T | PromiseLike<T>) => void
  reject: (reason?: unknown) => void
  promise: Task<T>
  weight: number
  signal?: AbortSignal
}

export type Task<T> = () => Promise<T>

export class PromiseThrottle<T> {
  private requestsPerSecond: number
  private lastStartTime: number = 0
  private queued: QueuedTask<T>[] = []

  constructor(options: PromiseThrottleOptions) {
    this.requestsPerSecond = options.requestsPerSecond
  }

  /**
   * @param task
   * @param options
   * @returns the promise returned by the task
   */
  add(
    task: Task<T>,
    options?: { signal?: AbortSignal; weight?: number },
  ): Promise<T> {
    const { signal, weight = 1 } = options ?? {}
    return new Promise<T>((resolve, reject) => {
      this.queued.push({ resolve, reject, promise: task, weight, signal })
      this.dequeue()
    })
  }

  /**
   * @param tasks
   * @param options
   * @returns a promise that gets resolved when all promises returned by `tasks` are resolved, or rejected when any task fails.
   */
  addAll(
    tasks: Task<T>[],
    options?: { signal?: AbortSignal; weight?: number },
  ): Promise<T[]> {
    const addedPromises = tasks.map((promise) => this.add(promise, options))
    return Promise.all(addedPromises)
  }

  private dequeue(): void {
    if (this.queued.length > 0) {
      const now = Date.now()
      const weight = this.queued[0].weight
      const inc = (1000 / this.requestsPerSecond) * weight
      const elapsed = now - this.lastStartTime

      if (elapsed >= inc) {
        this.execute()
      } else {
        setTimeout(() => {
          this.dequeue()
        }, inc - elapsed)
      }
    }
  }

  private execute(): void {
    this.lastStartTime = Date.now()
    const candidate = this.queued.shift()
    const aborted = candidate?.signal?.aborted
    if (!candidate) {
      return
    }
    if (aborted) {
      candidate.reject(new DOMException('', 'AbortError'))
    } else {
      candidate
        .promise()
        .then((result) => {
          // This is safe, because the type of result is the same as the type of resolve's argument. It is guaranteed in `add`/`addAll` methods.
          candidate.resolve(result)
        })
        .catch((error) => {
          candidate.reject(error)
        })
    }
  }
}

export default PromiseThrottle
